import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "../store";
import { RootState } from "../reducers/hooks";
import { alertMessage } from "../actions/common";
import { errorHandler } from "app/shared/Service/errorHandler";
import { getActiveConfig } from "app/utils/constants/common";
import { getLocalDateRangeFromFilters, getUtcRangeFromFilters } from "app/utils/analyticsDateRange";
import {
  getDeliveriesApi,
  getRevenueApi,
  getSignUpsApi,
} from "app/services/lightfieldsAnalytics";
import {
  LightfieldsAnalyticsFilters,
  PaymentMethodRevenue,
} from "app/types/lightfieldsAnalytics";

const isNotFoundError = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  "response" in error &&
  (error as { response?: { status?: number } }).response?.status === 404;

interface LightfieldsAnalyticsState {
  signUpCount: number | null;
  deliveryCount: number | null;
  totalRevenue: number | null;
  paymentMethods: PaymentMethodRevenue[];
  filters: LightfieldsAnalyticsFilters;
  isLoading: boolean;
}

const initialState: LightfieldsAnalyticsState = {
  signUpCount: null,
  deliveryCount: null,
  totalRevenue: null,
  paymentMethods: [],
  filters: { today: true },
  isLoading: false,
};

export const lightfieldsAnalyticsSlice = createSlice({
  name: "lightfieldsAnalytics",
  initialState,
  reducers: {
    setLightfieldsAnalyticsFilters: (
      state,
      action: PayloadAction<LightfieldsAnalyticsFilters>,
    ) => {
      state.filters = action.payload;
    },
    setLightfieldsAnalyticsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setLightfieldsAnalyticsData: (
      state,
      action: PayloadAction<{
        signUpCount: number | null;
        deliveryCount: number | null;
        totalRevenue: number | null;
        paymentMethods: PaymentMethodRevenue[];
      }>,
    ) => {
      state.signUpCount = action.payload.signUpCount;
      state.deliveryCount = action.payload.deliveryCount;
      state.totalRevenue = action.payload.totalRevenue;
      state.paymentMethods = action.payload.paymentMethods;
    },
    clearLightfieldsAnalyticsData: (state) => {
      state.signUpCount = null;
      state.deliveryCount = null;
      state.totalRevenue = null;
      state.paymentMethods = [];
    },
  },
});

export const {
  setLightfieldsAnalyticsFilters,
  setLightfieldsAnalyticsLoading,
  setLightfieldsAnalyticsData,
  clearLightfieldsAnalyticsData,
} = lightfieldsAnalyticsSlice.actions;

export default lightfieldsAnalyticsSlice.reducer;

export const fetchLightfieldsAnalyticsAction = () => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const { filters } = getState().lightfieldsAnalytics;
    const { storeId } = getActiveConfig();
    // Sign-ups and revenue use UTC ISO; deliveries uses local yyyy-MM-dd.
    const { fromUtc, toUtc } = getUtcRangeFromFilters(filters);
    const { fromDate: deliveriesFrom, toDate: deliveriesTo } =
      getLocalDateRangeFromFilters(filters);

    dispatch(setLightfieldsAnalyticsLoading(true));

    try {
      const [signUpsResult, deliveriesResult, revenueResult] =
        await Promise.allSettled([
          getSignUpsApi(storeId, fromUtc, toUtc),
          getDeliveriesApi(storeId, deliveriesFrom, deliveriesTo),
          getRevenueApi(storeId, fromUtc, toUtc),
        ]);

      let signUpCount: number | null = null;
      let deliveryCount: number | null = null;
      let totalRevenue: number | null = null;
      let paymentMethods: PaymentMethodRevenue[] = [];
      const errors: string[] = [];

      if (signUpsResult.status === "fulfilled") {
        signUpCount = signUpsResult.value.data.data.signUpCount;
      } else if (!isNotFoundError(signUpsResult.reason)) {
        errors.push(errorHandler(signUpsResult.reason));
      }

      if (deliveriesResult.status === "fulfilled") {
        deliveryCount = deliveriesResult.value.data.data.deliveryCount;
      } else if (!isNotFoundError(deliveriesResult.reason)) {
        errors.push(errorHandler(deliveriesResult.reason));
      }

      if (revenueResult.status === "fulfilled") {
        const revenueData = revenueResult.value.data.data;
        totalRevenue = revenueData.totalRevenue;
        paymentMethods = revenueData.paymentMethods ?? [];
      } else {
        errors.push(errorHandler(revenueResult.reason));
      }

      const hasAnyData =
        signUpCount !== null ||
        deliveryCount !== null ||
        totalRevenue !== null;

      if (hasAnyData) {
        dispatch(
          setLightfieldsAnalyticsData({
            signUpCount,
            deliveryCount,
            totalRevenue,
            paymentMethods,
          }),
        );
      } else {
        dispatch(clearLightfieldsAnalyticsData());
      }

      if (errors.length > 0) {
        dispatch(alertMessage(errors.join(". "), "error"));
      }
    } catch (error) {
      dispatch(clearLightfieldsAnalyticsData());
      dispatch(alertMessage(errorHandler(error), "error"));
    } finally {
      dispatch(setLightfieldsAnalyticsLoading(false));
    }
  };
};
